import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * KickoffEmailButton
 * ------------------
 * Compact per-row "send kickoff email" control for AdminRetailers.
 *
 * Behavior:
 *   - On click, fetches /admin/retailers/{id}/kickoff-history first.
 *   - If a "sent" record exists within the last 24 h, opens a confirm
 *     dialog ("Last sent {relative}. Send again?") to prevent double-emails.
 *   - Otherwise fires POST /admin/retailers/{id}/send-kickoff immediately.
 *
 * Props:
 *   retailerId (string, required)
 *   retailerLabel (string, optional) - display label for confirm text
 *   onSent (fn, optional) - called with server response on success
 */
export default function KickoffEmailButton({ retailerId, retailerLabel, onSent }) {
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastSend, setLastSend] = useState(null);

  const doSend = useCallback(async () => {
    setBusy(true);
    try {
      const res = await api.post(`/admin/retailers/${retailerId}/send-kickoff`);
      toast.success('Kickoff email sent.');
      if (onSent) onSent(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to send.';
      toast.error(msg);
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  }, [retailerId, onSent]);

  const parseSentAt = (iso) => {
    if (!iso) return NaN;
    // Backend returns Python .isoformat() strings like
    // "2026-07-17T23:00:00.123456+00:00" — some browsers reject the extra
    // µs digits. Trim to milliseconds to be safe.
    const cleaned = String(iso).replace(/(\.\d{3})\d+/, '$1');
    return new Date(cleaned).getTime();
  };

  const handleClick = async () => {
    setBusy(true);
    try {
      // Cache-buster query so we always read fresh log rows after a send.
      const res = await api.get(`/admin/retailers/${retailerId}/kickoff-history?ts=${Date.now()}`);
      const rows = res.data?.rows || [];
      const now = Date.now();
      const recentSent = rows.find(r => {
        if (r.status !== 'sent') return false;
        const t = parseSentAt(r.sent_at);
        return !isNaN(t) && (now - t) < 24 * 60 * 60 * 1000;
      });
      if (recentSent) {
        setLastSend(recentSent);
        setConfirmOpen(true);
        setBusy(false);
        return;
      }
    } catch (_) {
      // History fetch failed — proceed to send anyway; server has the log.
    }
    doSend();
  };

  const humanRelative = (iso) => {
    const t = parseSentAt(iso);
    if (isNaN(t)) return '';
    const mins = Math.round((Date.now() - t) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    return `${hrs} h ago`;
  };

  return (
    <>
      <Button
        data-testid={`send-kickoff-btn-${retailerId}`}
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={handleClick}
      >
        {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
        Kickoff email
      </Button>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="kickoff-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Resend kickoff email?</DialogTitle>
            <DialogDescription>
              A kickoff email was sent to <b>{retailerLabel || retailerId}</b> {humanRelative(lastSend?.sent_at)}.
              Sending again may look spammy to the retailer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              data-testid="kickoff-confirm-send-btn"
              onClick={doSend}
              disabled={busy}
            >
              {busy ? 'Sending…' : 'Send again'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
